/**
 * Capa 2 (V96) — "Facturar (CFDI)" sobre una orden PAGADA del POS.
 * El staff captura los datos fiscales del CLIENTE (o reusa uno ya capturado)
 * y emite: el CFDI sale a nombre del NEGOCIO del tenant (su emisor) hacia su
 * cliente, con XML+PDF por correo y descargables.
 * Doble enforcement: el botón se oculta sin `cfdi_sales:emit`, y el backend
 * valida permiso + módulo + emisor listo (403/503) de todos modos.
 */
import { useEffect, useState } from 'react';
import { api, apiDownload } from '../lib/api';
import { useAuth } from '../auth/AuthProvider';
import { FiscalData, FiscalDataFields, EMPTY_FISCAL, fiscalErrMsg } from './FiscalDataFields';

interface CustomerRow {
  id: string; rfc: string; razon_social: string; regimen_fiscal: string;
  uso_cfdi: string; cp: string; email: string | null;
}

interface EmitResult { cfdiId: string; status: string; folio: string | null; error: string | null }

function canEmit(user: any): boolean {
  if (!user) return false;
  if (user.role === 'SUPER_USER') return true;
  return Array.isArray(user.permissions) && user.permissions.includes('cfdi_sales:emit');
}

export function FacturarCfdiButton({ orderId }: { orderId: string }) {
  const { user } = useAuth() as any;
  const [open, setOpen] = useState(false);
  if (!canEmit(user)) return null;
  return (
    <>
      <button onClick={() => setOpen(true)}
        className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">
        Facturar (CFDI)
      </button>
      {open && <SalesCfdiDialog orderId={orderId} onClose={() => setOpen(false)} />}
    </>
  );
}

/**
 * Modo emisión (orderId) o modo REINTENTO (retryCfdiId, follow-up V98): un
 * fallo por receptor malo se corrige aquí — el dialog abre pre-llenado con el
 * receptor del CFDI (editable o reemplazable) y el retry re-timbra con el
 * receptor corregido.
 */
export function SalesCfdiDialog({ orderId, retryCfdiId, prefill, onClose }: {
  orderId?: string;
  retryCfdiId?: string;
  prefill?: { rfc?: string | null; razonSocial?: string | null };
  onClose: () => void;
}) {
  const isRetry = Boolean(retryCfdiId);
  const [existing, setExisting] = useState<CustomerRow[]>([]);
  const [selected, setSelected] = useState<CustomerRow | null>(null);
  const [form, setForm] = useState<FiscalData>({
    ...EMPTY_FISCAL,
    rfc: prefill?.rfc ?? '',
    razonSocial: prefill?.razonSocial ?? '',
  });
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [result, setResult] = useState<EmitResult | null>(null);
  const [savedMsg, setSavedMsg] = useState<string | null>(null);

  // Editar limpia el "guardado ✓" (los datos ya no son los persistidos).
  const editForm = (f: FiscalData) => { setForm(f); setSavedMsg(null); };
  const editEmail = (v: string) => { setEmail(v); setSavedMsg(null); };

  // Elegir un cliente guardado PRE-LLENA el formulario (editable) — nunca lo bloquea:
  // el registro puede traer un error o el cliente querer otro correo/RFC/régimen/CP. Al
  // emitir SIEMPRE se manda `customer` inline (el backend upsertea por tenant+rfc).
  const applyCustomer = (c: CustomerRow | null) => {
    setSelected(c);
    if (c) {
      setForm({ rfc: c.rfc, razonSocial: c.razon_social, regimenFiscal: c.regimen_fiscal, usoCfdi: c.uso_cfdi, cp: c.cp });
      setEmail(c.email ?? '');
    } else {
      setForm({ ...EMPTY_FISCAL, rfc: prefill?.rfc ?? '', razonSocial: prefill?.razonSocial ?? '' });
      setEmail('');
    }
  };

  useEffect(() => {
    api<{ items: CustomerRow[] }>('/cfdi-sales/customers')
      .then((r) => {
        setExisting(r.items);
        // Reintento: pre-llena con el cliente capturado del CFDI fallido para editar
        // sobre lo que ya hay (o elegir "datos nuevos" y re-capturar).
        if (isRetry && prefill?.rfc) {
          const match = r.items.find((c) => c.rfc === prefill.rfc);
          if (match) applyCustomer(match);
        }
      })
      .catch(() => { /* lista vacía: captura manual */ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = async () => {
    setBusy(true); setErr(null);
    try {
      // Siempre inline: los datos que el staff ve (y pudo editar) son los que se timbran.
      const body = {
        customer: { ...form, rfc: form.rfc.trim().toUpperCase(), cp: form.cp.trim(), email: email.trim() || undefined },
      };
      if (isRetry) {
        const r = await api<{ cfdi: { id: string; status: string; uuid: string | null; error: string | null } }>(
          `/cfdi-sales/${retryCfdiId}/retry`, { method: 'POST', body });
        setResult({
          cfdiId: r.cfdi?.id ?? retryCfdiId!,
          status: r.cfdi?.status ?? 'pending',
          folio: r.cfdi?.uuid ?? null,
          error: r.cfdi?.error ?? null,
        });
      } else {
        const r = await api<EmitResult>(`/cfdi-sales/from-order/${orderId}`, { method: 'POST', body });
        setResult(r);
      }
    } catch (e: any) {
      setErr(fiscalErrMsg(e, isRetry ? 'No se pudo reintentar la factura.' : 'No se pudo emitir la factura.'));
    } finally { setBusy(false); }
  };

  // Persiste la corrección (upsert por tenant+rfc) SIN emitir: así la re-selección del
  // cliente muestra la versión corregida, no la vieja. Refresca la lista local + selección.
  const saveCustomerData = async () => {
    setBusy(true); setErr(null);
    try {
      const saved = await api<CustomerRow>('/cfdi-sales/customers', {
        method: 'POST',
        body: { ...form, rfc: form.rfc.trim().toUpperCase(), cp: form.cp.trim(), email: email.trim() || undefined },
      });
      setExisting((prev) => [saved, ...prev.filter((c) => c.id !== saved.id)]);
      setSelected(saved);
      setSavedMsg('Datos del cliente guardados ✓');
    } catch (e: any) {
      setErr(fiscalErrMsg(e, 'No se pudieron guardar los datos del cliente.'));
    } finally { setBusy(false); }
  };

  const inputCls = 'w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 focus:border-slate-400 focus:outline-none';

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-slate-900/40 p-4">
      <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white p-6 shadow-xl">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="font-serif text-lg text-slate-900">
            {isRetry ? 'Reintentar factura (CFDI)' : 'Facturar venta (CFDI)'}
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700">✕</button>
        </div>

        {isRetry && !result && (
          <p className="mb-3 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
            Revisa y corrige los datos del receptor (RFC y razón social deben calzar EXACTO con su
            Constancia del SAT) antes de re-timbrar.
          </p>
        )}

        {result ? (
          <div className="space-y-3">
            {result.status === 'stamped' ? (
              <>
                <p className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
                  Factura emitida ✓ — folio fiscal: <strong className="break-all">{result.folio}</strong>.
                  Si el cliente dejó correo, ya va en camino con XML y PDF.
                </p>
                <div className="flex gap-2">
                  <button onClick={() => apiDownload(`/cfdi-sales/${result.cfdiId}/download/xml`, `CFDI-${result.folio}.xml`)}
                    className="flex-1 rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50">
                    Descargar XML
                  </button>
                  <button onClick={() => apiDownload(`/cfdi-sales/${result.cfdiId}/download/pdf`, `CFDI-${result.folio}.pdf`)}
                    className="flex-1 rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50">
                    Descargar PDF
                  </button>
                </div>
              </>
            ) : (
              <p className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                La factura quedó <strong>en cola</strong> ({result.status}). Se reintentará automáticamente;
                puedes verla después en la sección de facturas.
              </p>
            )}
            <div className="flex justify-end">
              <button onClick={onClose} className="rounded-xl bg-slate-900 px-3 py-2 text-sm font-medium text-white">Cerrar</button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {existing.length > 0 && (
              <div>
                <label className="text-[11px] font-medium uppercase tracking-[0.18em] text-slate-400">Cliente ya capturado</label>
                <select
                  value={selected?.id ?? ''}
                  onChange={(e) => applyCustomer(existing.find((c) => c.id === e.target.value) ?? null)}
                  className={inputCls}>
                  <option value="">— Capturar datos nuevos —</option>
                  {existing.map((c) => (
                    <option key={c.id} value={c.id}>{c.rfc} · {c.razon_social}</option>
                  ))}
                </select>
                <p className="mt-1 text-[11px] text-slate-400">
                  Precarga los datos guardados; puedes <strong>editarlos</strong> antes de emitir (correo, RFC, régimen, CP…).
                </p>
              </div>
            )}

            <FiscalDataFields form={form} onChange={editForm} compact />
            <div>
              <label className="text-[11px] font-medium uppercase tracking-[0.18em] text-slate-400">
                Correo del cliente (para enviarle XML + PDF)
              </label>
              <input value={email} onChange={(e) => editEmail(e.target.value)} type="email"
                placeholder="cliente@correo.com" className={inputCls} />
            </div>

            {savedMsg && <p className="text-sm text-emerald-600">{savedMsg}</p>}
            {err && <p className="text-sm text-rose-600">{err}</p>}
            {/* responsive-turno: flex-wrap — en 375px "Guardar datos" + "Cancelar" +
                "Emitir factura" no caben en una fila; sin wrap se comprimían/desbordaban. */}
            <div className="flex flex-wrap items-center justify-between gap-2">
              <button onClick={saveCustomerData} disabled={busy || !form.rfc.trim()}
                className="rounded-xl border border-slate-200 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                title="Guarda la corrección para reusarla luego (no emite la factura)">
                Guardar datos
              </button>
              <div className="flex gap-2">
                <button onClick={onClose} disabled={busy}
                  className="rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-600 hover:bg-slate-50">Cancelar</button>
                <button onClick={submit} disabled={busy}
                  className="rounded-xl bg-[var(--brand-primary)] px-3 py-2 text-sm font-medium text-[var(--brand-primary-fg)] hover:bg-[var(--brand-primary-hover)] disabled:opacity-50">
                  {busy ? 'Timbrando…' : isRetry ? 'Re-timbrar' : 'Emitir factura'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
